import {
  Permission,
  UserRole,
  RolePermissions,
  SubscriptionPlan,
} from '../types/auth';

// Define permissions for each role
const rolePermissions: RolePermissions = {
  [UserRole.USER]: [
    // Base user permissions
    Permission.VIEW_HOME,
    Permission.VIEW_OUTFITS,
    Permission.VIEW_ANALYSIS,
    Permission.VIEW_MOODBOARDS,
    Permission.VIEW_PROFILE,
    Permission.VIEW_OTHER_PROFILE,

    Permission.CREATE_ANALYSIS,
    Permission.CREATE_OUTFIT,
    Permission.CREATE_MOODBOARD,
    Permission.EDIT_PROFILE,
    Permission.EDIT_MOODBOARD,
    Permission.DELETE_MOODBOARD,
    Permission.LIKE_MOODBOARD,
    Permission.SAVE_OUTFIT,
  ],
  [UserRole.CLIENT]: [
    // Clients served by businesses - similar to users but may have limited features
    Permission.VIEW_HOME,
    Permission.VIEW_OUTFITS,
    Permission.VIEW_ANALYSIS,
    Permission.VIEW_MOODBOARDS,
    Permission.VIEW_PROFILE,
    Permission.VIEW_OTHER_PROFILE,

    Permission.CREATE_ANALYSIS,
    Permission.CREATE_OUTFIT,
    Permission.LIKE_MOODBOARD,
    Permission.SAVE_OUTFIT,
  ],
  [UserRole.STAFF]: [
    // Staff working under a business
    Permission.VIEW_HOME,
    Permission.VIEW_OUTFITS,
    Permission.VIEW_ANALYSIS,
    Permission.VIEW_MOODBOARDS,
    Permission.VIEW_PROFILE,
    Permission.VIEW_OTHER_PROFILE,

    Permission.CREATE_ANALYSIS,
    Permission.CREATE_OUTFIT,
    Permission.CREATE_MOODBOARD,
    Permission.EDIT_PROFILE,
    Permission.EDIT_MOODBOARD,
    Permission.DELETE_MOODBOARD,
    Permission.LIKE_MOODBOARD,
    Permission.SAVE_OUTFIT,
  ],
  [UserRole.BUSINESS_ADMIN]: [
    // Business admins manage their business account and clients
    Permission.VIEW_HOME,
    Permission.VIEW_OUTFITS,
    Permission.VIEW_ANALYSIS,
    Permission.VIEW_MOODBOARDS,
    Permission.VIEW_PROFILE,
    Permission.VIEW_OTHER_PROFILE,

    Permission.CREATE_ANALYSIS,
    Permission.CREATE_OUTFIT,
    Permission.CREATE_MOODBOARD,
    Permission.EDIT_PROFILE,
    Permission.EDIT_MOODBOARD,
    Permission.DELETE_MOODBOARD,
    Permission.LIKE_MOODBOARD,
    Permission.SAVE_OUTFIT,

    // Business admin specific permissions would go here
  ],
  [UserRole.MODERATOR]: [
    // Moderators manage content and flags
    Permission.VIEW_HOME,
    Permission.VIEW_OUTFITS,
    Permission.VIEW_ANALYSIS,
    Permission.VIEW_MOODBOARDS,
    Permission.VIEW_PROFILE,
    Permission.VIEW_OTHER_PROFILE,

    Permission.CREATE_ANALYSIS,
    Permission.CREATE_OUTFIT,
    Permission.CREATE_MOODBOARD,
    Permission.EDIT_PROFILE,
    Permission.EDIT_MOODBOARD,
    Permission.DELETE_MOODBOARD,
    Permission.LIKE_MOODBOARD,
    Permission.SAVE_OUTFIT,

    // Moderator specific permissions would go here
  ],
  [UserRole.SUPPORT]: [
    // Support staff handle user support and tickets
    Permission.VIEW_HOME,
    Permission.VIEW_OUTFITS,
    Permission.VIEW_ANALYSIS,
    Permission.VIEW_MOODBOARDS,
    Permission.VIEW_PROFILE,
    Permission.VIEW_OTHER_PROFILE,

    Permission.CREATE_ANALYSIS,
    Permission.CREATE_OUTFIT,
    Permission.CREATE_MOODBOARD,
    Permission.EDIT_PROFILE,
    Permission.EDIT_MOODBOARD,
    Permission.DELETE_MOODBOARD,
    Permission.LIKE_MOODBOARD,
    Permission.SAVE_OUTFIT,

    // Support specific permissions would go here
  ],
  [UserRole.PLATFORM_ADMIN]: [
    // Platform admins have full access
    Permission.VIEW_HOME,
    Permission.VIEW_OUTFITS,
    Permission.VIEW_ANALYSIS,
    Permission.VIEW_MOODBOARDS,
    Permission.VIEW_PROFILE,
    Permission.VIEW_OTHER_PROFILE,

    Permission.CREATE_ANALYSIS,
    Permission.CREATE_OUTFIT,
    Permission.CREATE_MOODBOARD,
    Permission.EDIT_PROFILE,
    Permission.EDIT_MOODBOARD,
    Permission.DELETE_MOODBOARD,
    Permission.LIKE_MOODBOARD,
    Permission.SAVE_OUTFIT,

    // Platform admin gets all subscription permissions
    Permission.BASIC_COLOR_ANALYSIS,
    Permission.FULL_COLOR_ANALYSIS,
    Permission.SAVE_COLOR_RESULTS,
    Permission.SHARE_COLOR_RESULTS,
    Permission.DAILY_DRAPE_TEST,
    Permission.UNLIMITED_DRAPE_TEST,
    Permission.SEASONAL_PALETTE_ACCESS,
    Permission.BASIC_RECOMMENDATIONS,
    Permission.PREMIUM_RECOMMENDATIONS,
    Permission.LIMITED_SMART_WARDROBE,
    Permission.UNLIMITED_SMART_WARDROBE,
    Permission.WARDROBE_EXPORT,
    Permission.AI_OUTFIT_SUGGESTIONS,
    Permission.LIMITED_FIT_CHECK,
    Permission.UNLIMITED_FIT_CHECK,
    Permission.LIMITED_COMMUNITY_ACCESS,
    Permission.FULL_COMMUNITY_ACCESS,
    Permission.LIMITED_COMMUNITY_QUESTIONS,
    Permission.UNLIMITED_COMMUNITY_QUESTIONS,
    Permission.PRIORITY_COMMUNITY_SUPPORT,
    Permission.LIMITED_MOODBOARDS,
    Permission.UNLIMITED_MOODBOARDS,
    Permission.PROFESSIONAL_MOODBOARD_TEMPLATES,
    Permission.AI_COLOR_CAPSULES,
    Permission.EARLY_ACCESS_FEATURES,
    Permission.INSIDER_STYLING_TIPS,
    Permission.DEEP_COLOR_ANALYSIS,
  ],
};

// Define permissions for each subscription plan
const subscriptionPermissions: { [key in SubscriptionPlan]: Permission[] } = {
  [SubscriptionPlan.FREE]: [
    // Free Tier – Explore Your Palette
    Permission.BASIC_COLOR_ANALYSIS, // 5 personalized color results & 3 "To Avoid" colors
    Permission.DAILY_DRAPE_TEST, // 1 Drape Test per day (primary + secondary)
    Permission.BASIC_RECOMMENDATIONS, // Basic makeup, hair, and nail recommendations
    Permission.LIMITED_SMART_WARDROBE, // Smart Wardrobe (up to 3 items)
    Permission.LIMITED_FIT_CHECK, // Fit Check (limited, with ads)
    Permission.LIMITED_COMMUNITY_ACCESS, // Limited access to the Community Moodboard
    Permission.LIMITED_COMMUNITY_QUESTIONS, // Ask up to 2 questions per day in the Community
    Permission.LIMITED_MOODBOARDS, // Basic moodboard creation
    Permission.CREATE_MOODBOARD, // Create moodboards
    Permission.EDIT_MOODBOARD, // Edit moodboards
    Permission.DELETE_MOODBOARD, // Delete moodboards
    Permission.LIKE_MOODBOARD, // Like moodboards
  ],

  [SubscriptionPlan.FREEMIUM_TRIAL]: [
    // Freemium Trial – Try the Full Experience (7 Days Free)
    // Includes everything in Free, plus:
    Permission.BASIC_COLOR_ANALYSIS,
    Permission.DAILY_DRAPE_TEST,
    Permission.BASIC_RECOMMENDATIONS,
    Permission.LIMITED_SMART_WARDROBE,
    Permission.LIMITED_FIT_CHECK,
    Permission.LIMITED_COMMUNITY_ACCESS,
    Permission.LIMITED_COMMUNITY_QUESTIONS,
    Permission.LIMITED_MOODBOARDS,
    Permission.CREATE_MOODBOARD, // Create moodboards
    Permission.EDIT_MOODBOARD, // Edit moodboards
    Permission.DELETE_MOODBOARD, // Delete moodboards
    Permission.LIKE_MOODBOARD, // Like moodboards

    // Additional freemium features:
    Permission.FULL_COLOR_ANALYSIS, // Full Color Analysis + Result Saving
    Permission.SAVE_COLOR_RESULTS, // Result Saving
    Permission.UNLIMITED_DRAPE_TEST, // Access to complete Drape Test Library
    Permission.SEASONAL_PALETTE_ACCESS, // Including seasonal palettes
    Permission.UNLIMITED_SMART_WARDROBE, // Unlimited Smart Wardrobe
    Permission.WARDROBE_EXPORT, // Export options
    Permission.UNLIMITED_MOODBOARDS, // Unlimited Moodboards
    Permission.FULL_COMMUNITY_ACCESS, // Full Community Access (no limits)
    Permission.UNLIMITED_COMMUNITY_QUESTIONS, // No question limits
  ],

  [SubscriptionPlan.PRO]: [
    // Pro Plan – Built for Creators, Stylists & Beauty Pros
    // Includes all Freemium features, plus:
    Permission.BASIC_COLOR_ANALYSIS,
    Permission.DAILY_DRAPE_TEST,
    Permission.BASIC_RECOMMENDATIONS,
    Permission.LIMITED_SMART_WARDROBE,
    Permission.LIMITED_FIT_CHECK,
    Permission.LIMITED_COMMUNITY_ACCESS,
    Permission.LIMITED_COMMUNITY_QUESTIONS,
    Permission.LIMITED_MOODBOARDS,
    Permission.CREATE_MOODBOARD, // Create moodboards
    Permission.EDIT_MOODBOARD, // Edit moodboards
    Permission.DELETE_MOODBOARD, // Delete moodboards
    Permission.LIKE_MOODBOARD, // Like moodboards
    Permission.FULL_COLOR_ANALYSIS,
    Permission.SAVE_COLOR_RESULTS,
    Permission.UNLIMITED_DRAPE_TEST,
    Permission.SEASONAL_PALETTE_ACCESS,
    Permission.UNLIMITED_SMART_WARDROBE,
    Permission.WARDROBE_EXPORT,
    Permission.UNLIMITED_MOODBOARDS,
    Permission.FULL_COMMUNITY_ACCESS,
    Permission.UNLIMITED_COMMUNITY_QUESTIONS,

    // Additional Pro features:
    Permission.SHARE_COLOR_RESULTS, // Save, share, and export personalized results
    Permission.AI_COLOR_CAPSULES, // Exclusive access to AI-generated Color Capsules
    Permission.AI_OUTFIT_SUGGESTIONS, // Unlimited wardrobe planning with AI outfit suggestions
    Permission.PROFESSIONAL_MOODBOARD_TEMPLATES, // Professional Moodboard creation (with premium templates)
    Permission.PRIORITY_COMMUNITY_SUPPORT, // Priority Community support + insider styling tips
    Permission.INSIDER_STYLING_TIPS, // Insider styling tips
    Permission.EARLY_ACCESS_FEATURES, // Early access to experimental features and new drops
    Permission.PREMIUM_RECOMMENDATIONS, // Enhanced recommendations
    Permission.UNLIMITED_FIT_CHECK, // No ads, unlimited fit check
  ],

  [SubscriptionPlan.PAY_PER_SCAN]: [
    // Pay-per-scan – One-time deep color analysis
    Permission.DEEP_COLOR_ANALYSIS, // One-time deep color analysis
    Permission.BASIC_RECOMMENDATIONS, // Basic recommendations for the analysis
    Permission.SAVE_COLOR_RESULTS, // Save the analysis results
    Permission.CREATE_MOODBOARD, // Create moodboards
    Permission.EDIT_MOODBOARD, // Edit moodboards
    Permission.DELETE_MOODBOARD, // Delete moodboards
    Permission.LIKE_MOODBOARD, // Like moodboards
  ],
};

class PermissionService {
  /**
   * Check if a user role has a specific permission
   * @param role - The user role to check
   * @param permission - The permission to check for
   * @returns boolean indicating if the role has the permission
   */
  static hasPermission(role: UserRole, permission: Permission): boolean {
    if (!role) {
      return false;
    }
    const permissions = rolePermissions[role];
    return permissions ? permissions.includes(permission) : false;
  }

  /**
   * Check if a user has premium features based on subscription plan
   * @param subscriptionPlan - The user's subscription plan
   * @param permission - The permission to check for
   * @returns boolean indicating if the subscription grants the permission
   */
  static hasSubscriptionPermission(
    subscriptionPlan: SubscriptionPlan,
    permission: Permission
  ): boolean {
    if (!subscriptionPlan) {
      return false;
    }
    const permissions = subscriptionPermissions[subscriptionPlan];
    return permissions ? permissions.includes(permission) : false;
  }

  /**
   * Check if a user has a permission considering both role and subscription
   * @param role - The user role to check
   * @param subscriptionPlan - The user's subscription plan
   * @param permission - The permission to check for
   * @returns boolean indicating if the user has the permission
   */
  static hasFullPermission(
    role: UserRole,
    subscriptionPlan: SubscriptionPlan,
    permission: Permission
  ): boolean {
    return (
      this.hasPermission(role, permission) &&
      this.hasSubscriptionPermission(subscriptionPlan, permission)
    );
  }

  /**
   * Check if a user role has multiple permissions
   * @param role - The user role to check
   * @param permissions - Array of permissions to check for
   * @returns boolean indicating if the role has all the specified permissions
   */
  static hasPermissions(role: UserRole, permissions: Permission[]): boolean {
    if (!role || !permissions || permissions.length === 0) {
      return false;
    }
    const rolePerms = rolePermissions[role];
    if (!rolePerms) {
      return false;
    }
    return permissions.every((permission) => rolePerms.includes(permission));
  }

  /**
   * Get all permissions for a role
   * @param role - The user role
   * @returns Array of permissions assigned to the role
   */
  static getPermissionsForRole(role: UserRole): Permission[] {
    if (!role) {
      return [];
    }
    return rolePermissions[role] || [];
  }

  /**
   * Get all permissions for a subscription plan
   * @param subscriptionPlan - The subscription plan
   * @returns Array of permissions assigned to the subscription plan
   */
  static getPermissionsForSubscription(
    subscriptionPlan: SubscriptionPlan
  ): Permission[] {
    if (!subscriptionPlan) {
      return [];
    }
    return subscriptionPermissions[subscriptionPlan] || [];
  }

  /**
   * Check if a user has access to premium features
   * @param subscriptionPlan - The user's subscription plan
   * @returns boolean indicating if the user has premium access
   */
  static hasPremiumAccess(subscriptionPlan: SubscriptionPlan): boolean {
    return (
      subscriptionPlan === SubscriptionPlan.PRO ||
      subscriptionPlan === SubscriptionPlan.FREEMIUM_TRIAL
    );
  }

  /**
   * Check if a user has pro-level access
   * @param subscriptionPlan - The user's subscription plan
   * @returns boolean indicating if the user has pro access
   */
  static hasProAccess(subscriptionPlan: SubscriptionPlan): boolean {
    return subscriptionPlan === SubscriptionPlan.PRO;
  }

  /**
   * Check if a user is on a trial
   * @param subscriptionPlan - The user's subscription plan
   * @returns boolean indicating if the user is on trial
   */
  static isOnTrial(subscriptionPlan: SubscriptionPlan): boolean {
    return subscriptionPlan === SubscriptionPlan.FREEMIUM_TRIAL;
  }

  /**
   * Check if a role has permission to view a specific screen
   * @param role - The user role
   * @param screenPermission - The permission required to view the screen
   * @returns boolean indicating if the role can access the screen
   */
  static canAccessScreen(
    role: UserRole,
    screenPermission: Permission
  ): boolean {
    return this.hasPermission(role, screenPermission);
  }

  /**
   * Get subscription tier name for display
   * @param subscriptionPlan - The subscription plan
   * @returns string name of the subscription tier
   */
  static getSubscriptionTierName(subscriptionPlan: SubscriptionPlan): string {
    switch (subscriptionPlan) {
      case SubscriptionPlan.FREE:
        return 'Free Tier';
      case SubscriptionPlan.FREEMIUM_TRIAL:
        return 'Freemium Trial';
      case SubscriptionPlan.PRO:
        return 'Pro Plan';
      case SubscriptionPlan.PAY_PER_SCAN:
        return 'Pay-per-scan';
      default:
        return 'Unknown';
    }
  }
}

export default PermissionService;
